/**
 * InputManager.js - Unified Input Management
 *
 * Combines all input sources (keyboard, mouse, gamepad) into a unified interface.
 * Provides:
 * - Combined movement input from all sources
 * - Unified aim handling (mouse or gamepad)
 * - Action state (shoot, reload, abilities)
 * - Input state for network synchronization
 */

import { ClientLogger } from '../logger/ClientLogger.js';
import { GamepadInput } from './GamepadInput.js';
import { KeyboardInput } from './KeyboardInput.js';
import { MouseInput } from './MouseInput.js';

const INPUT_PICKUP_INTERACT_RADIUS = 100;
const inputLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {} };
})();
const inputLogger = ClientLogger.getLogger('input') || inputLoggerFallback;

const InputManager = {
  // Sub-input handlers
  keyboard: null,
  mouse: null,
  gamepad: null,

  // Game state reference
  gameState: null,

  // Input state (synchronized with gameState.inputState)
  inputState: null,
  pendingGamepadReload: false,
  capturingText: false,

  // Callbacks for game actions
  callbacks: {
    onShoot: null,
    onReload: null,
    onAbility: null,
    onInventorySwitch: null,
    onSwitchToSlot: null,
    onPickup: null,
    onQuickSwapPickaxe: null,
    showNotification: null,
    canShoot: null,
    canReload: null,
    onFlag: null,
    onFlagCompose: null
  },

  /**
   * Initialize the input manager
   * @param {Object} options - Configuration options
   * @param {HTMLCanvasElement} options.canvas - Game canvas
   * @param {GameState} options.gameState - Game state reference
   * @param {Object} options.callbacks - Callback functions
   */
  init(options = {}) {
    this.gameState = options.gameState;
    this.inputState = this.gameState?.inputState || {
      up: false,
      down: false,
      left: false,
      right: false,
      mouse: { x: 0, y: 0, down: false },
      angle: 0,
      lastAimSend: 0,
      aimSeq: 0,
      lastAimSource: 'mouse',
      lastGamepadAngle: 0
    };
    this.pendingGamepadReload = false;

    // Store callbacks
    Object.assign(this.callbacks, options.callbacks || {});

    // Initialize gamepad (class instance)
    this.gamepad = new GamepadInput();

    // Initialize keyboard
    KeyboardInput.init({
      gameState: this.gameState,
      onKeyDown: (key, e, code) => this.handleKeyDown(key, e, code),
      onKeyUp: (key, e, code) => this.handleKeyUp(key, e, code)
    });
    this.keyboard = KeyboardInput;

    // Initialize mouse
    MouseInput.init({
      canvas: options.canvas,
      onMouseDown: (e, state) => this.handleMouseDown(e, state),
      onMouseUp: (e, state) => this.handleMouseUp(e, state),
      onMouseMove: (e, state) => this.handleMouseMove(e, state),
      onWheel: (direction, e) => this.handleMouseWheel(direction, e)
    });
    this.mouse = MouseInput;
  },

  /**
   * Poll gamepad state - call this every frame
   */
  poll() {
    this.gamepad.poll();
  },

  /**
   * Handle key down events
   */
  handleKeyDown(key, e, code) {
    inputLogger.debug('[InputManager] handleKeyDown:', { key, code });

    // Check for inventory toggle (TAB key) - handle both lowercase and the actual code
    // This needs to work even if not in game, so check it BEFORE isInGame()
    if (key === 'tab' || key === 'Tab' || code === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      if (this.gameState) {
        this.gameState.inventoryOpen = !this.gameState.inventoryOpen;
      }
      return;
    }

    if (!this.isInGame()) return;

    if (this.capturingText) return;

    const flagKey = (this.gameState?.uiConfig?.controls?.flagKey || 'F8');
    const composeKey = (this.gameState?.uiConfig?.controls?.flagComposeKey || 'F9');
    if (key === flagKey.toLowerCase() || code === flagKey || key === flagKey) {
      e.preventDefault();
      this.callbacks.onFlag?.();
      return;
    }
    if (key === composeKey.toLowerCase() || code === composeKey || key === composeKey) {
      e.preventDefault();
      this.callbacks.onFlagCompose?.();
      return;
    }

    // Large map overlay toggle (M). See-through tactical view: it never gates
    // movement, aim or shooting, so nothing else checks mapOpen.
    const mapKey = this.gameState?.uiConfig?.minimap?.toggleKey || 'm';
    if (key === mapKey) {
      if (this.gameState) {
        this.gameState.mapOpen = !this.gameState.mapOpen;
      }
      return;
    }

    // Leaderboard popup toggle (L). Same deal as the map: a glance at the
    // scores that never gates movement, aim or shooting.
    const leaderboardKey = this.gameState?.uiConfig?.leaderboard?.toggleKey || 'l';
    if (key === leaderboardKey) {
      if (this.gameState) {
        this.gameState.leaderboardOpen = !this.gameState.leaderboardOpen;
      }
      return;
    }

    // Direct slot select (1-5, matching the HUD's own slot labels 1-indexed
    // left to right — '1' is always the pickaxe, since it's slot 0).
    if (key >= '1' && key <= '5' && !this.isLocalPlayerDead()) {
      if (this.callbacks.onSwitchToSlot) {
        this.callbacks.onSwitchToSlot(Number(key) - 1);
      }
      return;
    }

    // Quick-swap to the pickaxe (always slot 0)
    if (key === 'v' && !this.isLocalPlayerDead()) {
      if (this.callbacks.onQuickSwapPickaxe) {
        this.callbacks.onQuickSwapPickaxe();
      }
      return;
    }

    // Check for pickup (e key)
    if (key === 'e' && !this.isLocalPlayerDead()) {
      if (this.callbacks.onPickup) {
        this.callbacks.onPickup();
      }
      return;
    }

    // Check for reload
    if (key === 'r' && !this.isLocalPlayerDead()) {
      if (this.callbacks.canReload) {
        const reloadCheck = this.callbacks.canReload();
        if (reloadCheck !== true) {
          if (reloadCheck && reloadCheck.name && this.callbacks.showNotification) {
            this.callbacks.showNotification(`Cannot reload while ${reloadCheck.name} is active!`, 'warning');
          }
          return;
        }
      }
    }

    // Update key state
    this.keyboard.keys[key] = true;
    if (code) {
      this.keyboard.keys[code] = true;
    }

    // Check for ability activation (try both key and code for special keys like Shift)
    const uiConfig = this.gameState?.uiConfig;
    const abilityKeybinds = uiConfig?.controls?.abilityKeybinds || {};
    inputLogger.debug('[InputManager] Ability keybinds:', abilityKeybinds);

    const abilityCategory = abilityKeybinds[key] || abilityKeybinds[code];
    inputLogger.debug('[InputManager] Checking ability:', { key, code, abilityCategory, isDead: this.isLocalPlayerDead() });

    if (abilityCategory && !this.isLocalPlayerDead()) {
      inputLogger.debug('[InputManager] Activating ability:', abilityCategory);
      if (this.callbacks.onAbility) {
        this.callbacks.onAbility(abilityCategory);
      }
    }
  },

  /**
   * Handle key up events
   */
  handleKeyUp(key, e, code) {
    this.keyboard.keys[key] = false;
    if (code) {
      this.keyboard.keys[code] = false;
    }
  },

  /**
   * Handle mouse down events
   */
  handleMouseDown(e, state) {
    if (!this.isInGame()) return;
    if (this.isLocalPlayerDead()) return;
    
    // Block shooting when inventory is open
    if (this.gameState.inventoryOpen) {
      return;
    }

    // Check if can shoot
    if (this.callbacks.canShoot) {
      const shootCheck = this.callbacks.canShoot();
      if (shootCheck !== true) {
        // If blocked by an ability with canShoot=false, deactivate it instead of shooting
        if (shootCheck && shootCheck.category && this.callbacks.onAbility) {
          inputLogger.debug(`[InputManager] Deactivating blocking ability: ${shootCheck.name} (${shootCheck.category})`);
          this.callbacks.onAbility(shootCheck.category);
          return;
        }
        
        if (shootCheck && shootCheck.name && this.callbacks.showNotification) {
          this.callbacks.showNotification(`Cannot shoot while ${shootCheck.name} is active!`, 'warning');
        }
        return;
      }
    }

    this.inputState.mouse.down = true;

    // Trigger shoot callback
    if (this.callbacks.onShoot) {
      this.callbacks.onShoot();
    }
  },

  /**
   * Handle mouse up events
   */
  handleMouseUp(e, state) {
    this.inputState.mouse.down = false;
  },

  /**
   * Handle mouse move events
   */
  handleMouseMove(e, state) {
    // Update mouse position
    this.inputState.mouse.x = state.x;
    this.inputState.mouse.y = state.y;

    // The stick wins only while it is actually deflected, so small mouse movements
    // can't fight an active gamepad aim. The moment it re-centres the mouse takes aim
    // back — a source that latched on couldn't be handed back without a page reload.
    const gamepadAim = this.gamepad?.getAimInput();
    if (gamepadAim?.active) {
      this.inputState.lastAimSource = 'gamepad';
      return;
    }
    this.inputState.angle = state.angle;
    this.inputState.lastAimSource = 'mouse';
  },

  /**
   * Handle mouse wheel events (for inventory switching)
   * @param {number} direction - 1 for scroll down (next), -1 for scroll up (previous)
   * @param {WheelEvent} e - Wheel event
   */
  handleMouseWheel(direction, e) {
    if (!this.isInGame()) return;
    if (this.isLocalPlayerDead()) return;

    // Call inventory switch callback
    if (this.callbacks.onInventorySwitch) {
      this.callbacks.onInventorySwitch(direction);
    }
  },

  /**
   * Handle gamepad button inputs
   * Call this every frame
   */
  handleGamepadButtons() {
    if (!this.gamepad.connected || !this.isInGame()) return;
    if (this.isLocalPlayerDead()) return;
    const player = this._getLocalPlayer();
    if (!player) return;

    // Shooting (RT Trigger)
    if (this.gamepad.isTriggerPressed('RT')) {
      if (this.callbacks.canShoot) {
        const shootCheck = this.callbacks.canShoot();
        if (shootCheck === true && this.callbacks.onShoot) {
          this.callbacks.onShoot();
        }
      }
    }

    // Inventory scrolling
    if (this.gamepad.isButtonJustPressed('LB')) {
      if (this.callbacks.onInventorySwitch) {
        this.callbacks.onInventorySwitch(-1);
      }
    }
    if (this.gamepad.isButtonJustPressed('RB')) {
      if (this.callbacks.onInventorySwitch) {
        this.callbacks.onInventorySwitch(1);
      }
    }

    // Pickup or reload (X button)
    if (this.gamepad.isButtonJustPressed('X')) {
      if (this.callbacks.onPickup) {
        this.callbacks.onPickup();
      }
      if (!this._hasPickupNearby(player)) {
        this._queueGamepadReload();
      }
    }

    // Ability - Movement (B)
    if (this.gamepad.isButtonJustPressed('B')) {
      if (this.callbacks.onAbility) {
        this.callbacks.onAbility('movement');
      }
    }

    // Ability - Utility (Y)
    if (this.gamepad.isButtonJustPressed('Y')) {
      if (this.callbacks.onAbility) {
        this.callbacks.onAbility('utility');
      }
    }
  },

  /**
   * Gather current input state from all sources
   * @returns {Object} - Combined input state
   */
  gatherInput() {
    const pendingReload = this.pendingGamepadReload;
    this.pendingGamepadReload = false;
    // Get keyboard movement
    const keyMovement = this.keyboard.getMovementInput();

    // Get gamepad movement
    const gamepadMovement = this.gamepad.getMovementInput();

    // Combine movement (keyboard OR gamepad)
    const up = keyMovement.up || gamepadMovement.up;
    const down = keyMovement.down || gamepadMovement.down;
    const left = keyMovement.left || gamepadMovement.left;
    const right = keyMovement.right || gamepadMovement.right;

    // Handle aiming - gamepad overrides mouse if active
    let angle = this.inputState.angle;
    const gamepadAim = this.gamepad.getAimInput();

    if (gamepadAim.active) {
      angle = gamepadAim.angle;
      this.inputState.angle = angle;
      this.inputState.lastAimSource = 'gamepad';
      this.inputState.lastGamepadAngle = angle;
    } else if (this.inputState.lastAimSource === 'gamepad') {
      // Keep gamepad angle when stick is released
      angle = this.inputState.lastGamepadAngle;
      this.inputState.angle = angle;
    }

    // Check for reload
    const reload = this.keyboard.isReloadPressed() || pendingReload;

    // Drift: held, not tapped — the shared model drains the meter while it is held
    const drift = this.keyboard.isDriftPressed() || (this.gamepad.isDriftPressed?.() ?? false);

    return {
      up,
      down,
      left,
      right,
      drift,
      reload,
      mouseX: this.inputState.mouse.x,
      mouseY: this.inputState.mouse.y,
      angle
    };
  },

  /**
   * Check if currently in game
   * @returns {boolean}
   */
  isInGame() {
    return this.gameState?.isInGame || false;
  },

  /**
   * Check if local player is dead
   * @returns {boolean}
   */
  isLocalPlayerDead() {
    return this.gameState?.isLocalPlayerDead() || false;
  },

  /**
   * Clear all input states
   */
  clearAllInput() {
    if (this.keyboard?.clearAllKeys) {
      this.keyboard.clearAllKeys();
    }
    if (this.inputState?.mouse) {
      this.inputState.mouse.down = false;
    }
    this.pendingGamepadReload = false;
  },

  /**
   * Get reference to keyboard handler
   * @returns {Object}
   */
  getKeyboard() {
    return this.keyboard;
  },

  /**
   * Get reference to mouse handler
   * @returns {Object}
   */
  getMouse() {
    return this.mouse;
  },

  /**
   * Get reference to gamepad handler
   * @returns {GamepadInput}
   */
  getGamepad() {
    return this.gamepad;
  },

  _getLocalPlayer() {
    const players = this.gameState?.players;
    const localId = this.gameState?.localId;
    if (!players || !localId) return null;
    return players.get ? players.get(localId) : null;
  },

  _hasPickupNearby(player, radius = INPUT_PICKUP_INTERACT_RADIUS) {
    if (!player) return false;
    const objects = this.gameState?.worldObjects;
    if (!objects) return false;
    const radiusSq = radius * radius;
    const iterable = objects instanceof Map ? objects.values() : objects;
    for (const obj of iterable) {
      if (!obj) continue;
      const type = typeof obj.type === 'string' ? obj.type : '';
      if (!type.includes('pickup')) continue;
      if (typeof obj.x !== 'number' || typeof obj.y !== 'number') continue;
      const dx = obj.x - player.x;
      const dy = obj.y - player.y;
      if ((dx * dx + dy * dy) <= radiusSq) {
        return true;
      }
    }
    return false;
  },

  _queueGamepadReload() {
    if (this.pendingGamepadReload) return;
    if (this.callbacks.canReload) {
      const reloadCheck = this.callbacks.canReload();
      if (reloadCheck !== true) {
        if (reloadCheck && reloadCheck.name && this.callbacks.showNotification) {
          this.callbacks.showNotification(`Cannot reload while ${reloadCheck.name} is active!`, 'warning');
        }
        return;
      }
    }
    this.pendingGamepadReload = true;
  }
};

// Export for use in other modules
// For now, InputManager is a global object loaded via script tag

export { INPUT_PICKUP_INTERACT_RADIUS, inputLoggerFallback, inputLogger, InputManager };
